import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../../core/_internal/factory.js';
import { Title } from '../../core/Title/Title.js';
import { Text } from '../../core/Text/Text.js';
import './PageHeader.css';

export interface PageHeaderProps extends BaseProps {
  /** Page title: rendered as an h2. */
  title: ReactNode;
  /** Optional supporting description below the title. */
  description?: ReactNode;
  /** Eyebrow / category label above the title. */
  eyebrow?: ReactNode;
  /** Breadcrumb element (typically `<BreadcrumbTrail />`). Rendered above the title. */
  breadcrumbs?: ReactNode;
  /** Right-aligned actions (buttons, menu, etc.). */
  actions?: ReactNode;
  /** Optional content rendered below the header (e.g. tabs or a filter bar). */
  children?: ReactNode;
  /** Removes the bottom divider when false. Default true. */
  withDivider?: boolean;
}

/**
 * Shared page chrome. Combines breadcrumbs + eyebrow + title + description +
 * actions into one consistent header, so every page in an app feels related.
 *
 *   <PageHeader
 *     breadcrumbs={<BreadcrumbTrail />}
 *     title="Tenants"
 *     description="Manage organisations on the platform."
 *     actions={<Button>Add tenant</Button>}
 *   />
 */
export const PageHeader = forwardRef(function PageHeader(
  { title, description, eyebrow, breadcrumbs, actions, children, withDivider = true, ...rest }: PageHeaderProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const base = useBaseAttrs('rr-page-header', rest, [withDivider && 'rr-page-header--divided']);
  const { dom } = splitBaseProps(rest);
  return (
    <header {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      {breadcrumbs ? <div className="rr-page-header__breadcrumbs">{breadcrumbs}</div> : null}
      <div className="rr-page-header__row">
        <div className="rr-page-header__heading">
          {eyebrow ? <Text role="overline" className="rr-page-header__eyebrow">{eyebrow}</Text> : null}
          <Title order={2} className="rr-page-header__title">{title}</Title>
          {description ? <Text role="bodySmall" className="rr-page-header__description">{description}</Text> : null}
        </div>
        {actions ? <div className="rr-page-header__actions">{actions}</div> : null}
      </div>
      {children ? <div className="rr-page-header__extras">{children}</div> : null}
    </header>
  );
});
